<template>
  <GenericCard
    :locked="locked"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.mailing.$invalid"
    :dirty="$v.mailing.$anyDirty"
    title="Mailing Address"
    :section="mailing"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked || mailing.same_as_physical"
      @submit.prevent="submitForm"
    >
      <ZipableAddressFields
        v-model="mailing"
        class="pa-0 mb-4"
        :locked="locked"
      />
    </v-form>
    <template #header>
      <v-checkbox
        v-model="mailing.same_as_physical"
        :disabled="locked || pePhysicalAddressCardIsEmpty"
        label="Same as physical address"
        class="shrink mr-4"
      />
    </template>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'
import ZipableAddressFields from '@/components/Party/PartyEdit/SectionCards/PersonalDataCards/ZipableAddressFields.vue'

export default {
  name: 'MailingAddressCard',

  components: {
    GenericCard,
    ZipableAddressFields,
  },

  props: {
    item: {
      type: Object,
      default: null,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      cached_a_number: null,
      cached_ssn: null,
      section: {},
    }
  },

  validations () {
    return {
      mailing: {
        address: {},
        address_type: {},
        city: {},
        country_id: {},
        county: {},
        same_as_physical: {},
        state: {},
        zip: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return 'mailing-address-card' },

    locked: {
      get () {
        return this.peMailingAddressCardLock
      },

      set (value) {
        this.setPeMailingAddressCardLock(value)
      },
    },

    mailing () { return this.section || {} },
    optionsCountries () { return this.getCachedDropdown('countries') },
    optionsStates () { return this.getCachedDropdown('us_state/states') },

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peMailingAddressCardLock',
      'pePhysicalAddressCardIsEmpty',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },

    'mailing.same_as_physical': {
      handler: function () {
        this.peMailingAddressCardSameAsPhysical({ section: this.mailing })
      },

      immediate: true,
    },
  },

  created () {
    this.initPeMailingAddressCard()

    this.getDropdownOptions({ path: 'us_state/states' })
    this.getDropdownOptions({ options: false, path: 'countries' })
    if (this.mailing?.same_as_physical) this.peMailingAddressCardSameAsPhysical({ section: this.mailing })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardRefresh () {
      await this.peMailingAddressCardRefresh({ id: this.section.id })
      if (this.mailing?.same_as_physical) this.peMailingAddressCardSameAsPhysical({ section: this.mailing })
    },

    async onCardUpdate () {
      await this.peMailingAddressCardUpdate({ id: this.section.id, section: this.mailing })
      if (this.mailing?.same_as_physical) this.peMailingAddressCardSameAsPhysical({ section: this.mailing })
    },

    async onSectionUnlock () {
      if (this.pePhysicalAddressCardIsEmpty) this.mailing.same_as_physical = false
      return this.setPeMailingAddressCardLock(false)
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'setPeMailingAddressCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeMailingAddressCard',
      'peMailingAddressCardRefresh',
      'peMailingAddressCardSameAsPhysical',
      'peMailingAddressCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
